import { createTheme, type ThemeOptions } from '@mui/material/styles'

// ============================================================
// Studio Design System tokens
// Cyan primary · Terracotta accent · Warm stone neutrals
// Mirrors reach3insight-app/packages/shared/src/theme/index.ts
// ============================================================

const PRIMARY_CYAN = '#008FA8'
const PRIMARY_CYAN_HOVER = '#006D83'
const PRIMARY_CYAN_LIGHT = '#7BD2E4'

const SECONDARY_TERRACOTTA = '#E85D3C'
const SECONDARY_TERRACOTTA_HOVER = '#C7442E'
const SECONDARY_TERRACOTTA_LIGHT = '#E8937A'

const SUCCESS = '#2E8B5C'
const WARNING = '#B27A28'
const DANGER = '#C7442E'
const INFO = '#3A6E8F'

const STONE_50 = '#fdfcf8'
const STONE_100 = '#f5f3ec'
const STONE_200 = '#e8e4d8'
const STONE_400 = '#a8a194'
const STONE_500 = '#7a7466'
const STONE_700 = '#3d3932'
const STONE_800 = '#28241f'
const STONE_900 = '#1a1714'

const DARK_SURFACE = '#1a1714'
const DARK_SURFACE_RAISED = '#252420'
const DARK_TEXT = '#f5f3ec'
const DARK_TEXT_SECONDARY = '#d4cec0'
const DARK_PRIMARY = '#5CB7C9'

const FONT_SANS = '"Source Sans 3 Variable", "Source Sans 3", "Helvetica Neue", Arial, sans-serif'
const FONT_SERIF = '"Source Serif 4 Variable", "Source Serif 4", Georgia, "Times New Roman", serif'

const getThemeOptions = (mode: 'light' | 'dark'): ThemeOptions => ({
  palette: {
    mode,
    primary: {
      main: mode === 'light' ? PRIMARY_CYAN : DARK_PRIMARY,
      light: PRIMARY_CYAN_LIGHT,
      dark: PRIMARY_CYAN_HOVER,
      contrastText: '#ffffff',
    },
    secondary: {
      main: SECONDARY_TERRACOTTA,
      light: SECONDARY_TERRACOTTA_LIGHT,
      dark: SECONDARY_TERRACOTTA_HOVER,
      contrastText: '#ffffff',
    },
    success: {
      main: SUCCESS,
      light: '#6BB394',
      dark: '#1F5C3D',
      contrastText: '#ffffff',
    },
    info: {
      main: INFO,
      light: '#7A9DBA',
      dark: '#214459',
      contrastText: '#ffffff',
    },
    error: {
      main: DANGER,
      light: '#E8937A',
      dark: '#7D2819',
      contrastText: '#ffffff',
    },
    warning: {
      main: WARNING,
      light: '#D4A862',
      dark: '#6E4912',
      contrastText: '#ffffff',
    },
    background: {
      default: mode === 'light' ? STONE_50 : DARK_SURFACE,
      paper: mode === 'light' ? '#ffffff' : DARK_SURFACE_RAISED,
    },
    text: {
      primary: mode === 'light' ? STONE_700 : DARK_TEXT,
      secondary: mode === 'light' ? STONE_500 : DARK_TEXT_SECONDARY,
    },
    divider: mode === 'light' ? STONE_200 : 'rgba(255,255,255,0.08)',
    grey: {
      50: STONE_50,
      100: STONE_100,
      200: STONE_200,
      300: '#d4cec0',
      400: STONE_400,
      500: STONE_500,
      600: '#57524a',
      700: STONE_700,
      800: STONE_800,
      900: STONE_900,
    },
  },
  typography: {
    fontFamily: FONT_SANS,
    h1: { fontFamily: FONT_SERIF, fontWeight: 600, fontSize: '2.5rem', lineHeight: 1.2, letterSpacing: '-0.02em' },
    h2: { fontFamily: FONT_SERIF, fontWeight: 600, fontSize: '2rem', lineHeight: 1.2, letterSpacing: '-0.015em' },
    h3: { fontFamily: FONT_SANS, fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.35, letterSpacing: '-0.01em' },
    h4: { fontFamily: FONT_SANS, fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.35 },
    h5: { fontFamily: FONT_SANS, fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.35 },
    h6: { fontFamily: FONT_SANS, fontWeight: 600, fontSize: '1rem', lineHeight: 1.35 },
    body1: { fontSize: '1rem', lineHeight: 1.65 },
    body2: { fontSize: '0.875rem', lineHeight: 1.65 },
    caption: { fontSize: '0.75rem' },
    overline: { fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' },
    button: { fontFamily: FONT_SANS, fontWeight: 600, textTransform: 'none' as const },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10, padding: '8px 16px' },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 14,
          boxShadow: 'none',
          border: `1px solid ${theme.palette.divider}`,
          backgroundImage: 'none',
          transition: 'border-color 200ms ease-in-out',
          '&:hover': {
            borderColor: theme.palette.mode === 'light' ? STONE_400 : 'rgba(255,255,255,0.16)',
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 18 } },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 6 } },
    },
    MuiTextField: {
      styleOverrides: {
        root: { '& .MuiOutlinedInput-root': { borderRadius: 10 } },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase' as const,
          letterSpacing: '0.08em',
          color: mode === 'light' ? STONE_500 : DARK_TEXT_SECONDARY,
        },
      },
    },
  },
})

export const createAppTheme = (mode: 'light' | 'dark') => createTheme(getThemeOptions(mode))
export const theme = createAppTheme('light')
